/**
 * ImportModal - Modal de importação de planilhas Excel
 */
import { useState, useMemo, useRef } from 'react'
import Modal from '@components/shared/Modal'
import OverflowAwareContainer from '@components/shared/OverflowAwareContainer'
import { usePrazos } from '@context/PrazosContext'
import { useNotification } from '@context/NotificationContext'
import ExcelParserService from '@services/excelParser'

export default function ImportModal({ isOpen, onClose, existingPrazos }) {
  const prazosCtx = usePrazos()
  const notify = useNotification()
  const [excelData, setExcelData] = useState([])
  const [fileName, setFileName] = useState('')
  const [importing, setImporting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const normNum = (n) => String(n || '').replace(/[^0-9]/g, '')

  const existingSet = useMemo(() => {
    return new Set((existingPrazos || []).map((p) => normNum(p.numeroProcesso)))
  }, [existingPrazos])

  const novos = useMemo(() => {
    return excelData.filter((p) => !existingSet.has(normNum(p.numeroProcesso)))
  }, [excelData, existingSet])

  const dupes = useMemo(() => {
    return excelData.filter((p) => existingSet.has(normNum(p.numeroProcesso)))
  }, [excelData, existingSet])

  const processFile = (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['xlsx', 'xls'].includes(ext)) {
      notify.showWarning('Selecione um arquivo .xlsx ou .xls')
      return
    }
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = ExcelParserService.parseLegalManagerXlsx(ev.target.result)
        if (!data.length) {
          notify.showWarning('Nenhum prazo encontrado no arquivo')
          return
        }
        setExcelData(data)
      } catch (err) {
        notify.showError('Erro ao processar arquivo: ' + err.message)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleImport = async () => {
    if (novos.length === 0) return
    setImporting(true)
    try {
      const results = await prazosCtx.importPrazos(novos)
      notify.showSuccess(`${results.imported} prazos importados com sucesso!`)
      setTimeout(() => {
        onClose()
        setExcelData([])
        setFileName('')
      }, 1500)
    } catch (err) {
      notify.showError('Erro na importação: ' + err.message)
    } finally {
      setImporting(false)
    }
  }

  const handleClose = () => {
    setExcelData([])
    setFileName('')
    onClose()
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    const [y, m, d] = iso.split('T')[0].split('-')
    return `${d}/${m}/${y}`
  }

  const footer = (
    <>
      <button className="btn-modern btn-modern-cancel" onClick={handleClose}>
        <i className="fas fa-times" /> Cancelar
      </button>
      <button
        className="btn-modern btn-modern-import"
        disabled={novos.length === 0 || importing}
        onClick={handleImport}
      >
        {importing ? (
          <>
            <i className="fas fa-spinner fa-spin" /> Importando...
          </>
        ) : (
          <>
            <i className="fas fa-upload" /> Importar Prazos
          </>
        )}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar Prazos do Excel"
      subtitle="Faça upload de um arquivo .xlsx ou .xls"
      icon="fa-file-excel"
      size="lg"
      footer={footer}
    >
      {/* Dropzone */}
      <div
        className={`modern-dropzone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0])
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="modern-dropzone-inner">
          <div className="dropzone-icon-wrap">
            <i className="fas fa-cloud-upload-alt" />
          </div>
          <h3>Arraste o arquivo Excel aqui</h3>
          <p>ou clique para selecionar</p>
          <button
            type="button"
            className="btn-modern-upload"
            onClick={(e) => {
              e.stopPropagation()
              fileInputRef.current?.click()
            }}
          >
            <i className="fas fa-folder-open" /> Selecionar Arquivo
          </button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls"
            className="is-visually-hidden"
            onChange={(e) => {
              if (e.target.files[0]) processFile(e.target.files[0])
            }}
          />
        </div>
        {fileName && (
          <div className="modern-file-info">
            📄 {fileName} — {excelData.length} registro(s) lido(s)
          </div>
        )}
      </div>

      {/* Preview */}
      {excelData.length > 0 && (
        <div className="import-preview-block">
          <div className="import-preview-header">
            <h4>📊 Resumo da Importação</h4>
            <p>
              Arquivo: {fileName} | Total: {excelData.length}
            </p>
          </div>

          {/* Stats */}
          <div className="import-stats">
            <div className="stat-card stat-success import-stat-card">
              <div className="stat-number import-stat-number">
                {novos.length}
              </div>
              <div className="stat-label">Novos Prazos</div>
              <div>✅</div>
            </div>
            {dupes.length > 0 && (
              <div className="stat-card stat-warning import-stat-card">
                <div className="stat-number import-stat-number">
                  {dupes.length}
                </div>
                <div className="stat-label">Duplicados</div>
                <div>⚠️</div>
              </div>
            )}
            <div className="stat-card stat-info import-stat-card">
              <div className="stat-number import-stat-number">
                {excelData.length}
              </div>
              <div className="stat-label">Total no Arquivo</div>
              <div>📄</div>
            </div>
          </div>

          {/* Badges */}
          <div className="import-summary-badges">
            <span className="badge badge-success">✅ {novos.length} novos prazos</span>
            {dupes.length > 0 && (
              <span className="badge badge-warning">⚠️ {dupes.length} duplicados (serão ignorados)</span>
            )}
          </div>

          {/* Table Preview */}
          {novos.length > 0 && (
            <div className="preview-section">
              <h5>👁️ Visualização dos {Math.min(novos.length, 10)} primeiros prazos:</h5>
              <OverflowAwareContainer className="import-table-wrap">
                <table className="import-table">
                  <thead>
                    <tr>
                      <th>Processo</th>
                      <th>Advogado</th>
                      <th>Descrição</th>
                      <th>Publicação</th>
                      <th>Vencimento</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {novos.slice(0, 10).map((p, i) => (
                      <tr key={i}>
                        <td className="import-table-process-number">
                          {(p.numeroProcesso || '').slice(-10)}
                        </td>
                        <td>{p.advogado || ''}</td>
                        <td title={p.descricao || ''}>
                          {(p.descricao || '').slice(0, 40)}
                          {(p.descricao || '').length > 40 ? '…' : ''}
                        </td>
                        <td>{formatDate(p.dataPublicacao)}</td>
                        <td>{formatDate(p.dataVencimento)}</td>
                        <td>
                          <span className={`badge badge-${p.status || 'pendente'}`}>{p.status || 'pendente'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </OverflowAwareContainer>
              {novos.length > 10 && (
                <p className="import-preview-note">
                  … e mais {novos.length - 10} registro(s) não visualizados
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
